import { MainNav } from "@/components/layout/MainNav";
import { Footer } from "@/components/layout/Footer";
import { AdminBar } from "@/components/admin";
import { ImpersonationBanner } from "@/components/admin/ImpersonationBanner";
import { EnterpriseEmulationBanner } from "@/components/admin/enterprise/EnterpriseEmulationBanner";
import { CampaignWizardDialog } from "@/components/email/campaigns/CampaignWizardDialog";
import { ConsentModal } from "@/components/legal/ConsentModal";
import { useAuth } from "@/hooks/useAuth";
import { useFoundationalTour } from "@/components/onboarding/useFoundationalTour";
import { useNotificationRealtime } from "@/hooks/useNotificationRealtime";
import { useProductUpdateNotification } from "@/hooks/useProductUpdateNotification";
import { Navigate, Outlet } from "react-router-dom";
import { isHermesUiEnabled } from "@/lib/feature-flags";

interface DashboardLayoutProps {
  searchValue?: string;
  onSearch?: (value: string) => void;
}

export function DashboardLayout({ searchValue, onSearch }: DashboardLayoutProps) {
  const { user, loading } = useAuth();
  // Auto-start the foundational onboarding tour for new users. The hook
  // handles its own gating against user_onboarding and is a no-op for
  // returning users who completed or permanently dismissed v1.
  useFoundationalTour();
  // Toast + bell-badge refresh on new in-app notifications (Supabase Realtime).
  useNotificationRealtime();
  // Emit a "What's new" notification once per release version, on next login.
  useProductUpdateNotification();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <ImpersonationBanner />
      <EnterpriseEmulationBanner />
      <AdminBar />
      <MainNav searchValue={searchValue} onSearch={onSearch} />
      <main className="flex-1 flex flex-col">
        <Outlet />
      </main>
      <Footer />
      {/* CampaignWizardDialog gated by VITE_HERMES_UI_ENABLED (Phase 3 fix 3.D). */}
      {isHermesUiEnabled() && <CampaignWizardDialog />}
      <ConsentModal />
    </div>
  );
}